import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES, LANGUAGE_CONFIG, type SupportedLanguage } from "../i18n";
import { stripLocaleFromPath, buildLocalePath } from "../lib/locale-utils";

interface Props {
  /**
   * Restrict the switcher to a subset of supported languages. Used by SEO
   * landing pages that only ship in a few locales — showing the full list
   * there would imply translations we don't have.
   */
  availableLanguages?: ReadonlyArray<SupportedLanguage>;
}

export default function LanguageSwitcher({ availableLanguages }: Props = {}) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentLang = (i18n.language?.slice(0, 2) ?? "en") as SupportedLanguage;
  const config = LANGUAGE_CONFIG[currentLang] ?? LANGUAGE_CONFIG.en;
  const languages = (availableLanguages ?? SUPPORTED_LANGUAGES) as ReadonlyArray<SupportedLanguage>;

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleChange = (lang: SupportedLanguage) => {
    // Persist preference in localStorage for i18next sync
    localStorage.setItem("wc_language", lang);
    setOpen(false);
    // Navigate to the new locale-prefixed URL (full page nav so prerendered HTML loads)
    const basePath = stripLocaleFromPath(window.location.pathname);
    const newPath = buildLocalePath(lang, basePath);
    window.location.assign(newPath);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer px-2 py-1 rounded-md hover:bg-muted/40"
        aria-label={`${config.flag} ${config.name}`}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="text-base leading-none">{config.flag}</span>
        <span className="hidden sm:inline">{config.name}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute end-0 top-full mt-1 z-50 w-48 max-h-80 overflow-y-auto rounded-lg border border-border/60 bg-card shadow-lg py-1"
          role="listbox"
          aria-label="Select language"
        >
          {languages.map((lang) => {
            const lc = LANGUAGE_CONFIG[lang];
            const isActive = lang === currentLang;
            return (
              <button
                key={lang}
                onClick={() => handleChange(lang)}
                role="option"
                aria-selected={isActive}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors cursor-pointer ${
                  isActive
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-foreground/80 hover:bg-muted/40"
                }`}
              >
                <span className="text-base leading-none">{lc.flag}</span>
                <span>{lc.name}</span>
                {isActive && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    className="ms-auto"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
